package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.ResizeParams
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.IconSize
import com.siriusxm.pia.components.IconType
import com.siriusxm.pia.components.currentTimeZoneId
import com.siriusxm.pia.components.icon
import com.siriusxm.pia.components.toLocalDateTimeIn
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.unifiedcontent.getString
import contentingestion.unifiedmodel.ChannelFlag
import contentingestion.unifiedmodel.EpisodeFlag
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Instant

// Mapping from flag IDs to Material Design icon names
private val flagIconMapping = mapOf(
    EpisodeFlag.REGEN_EPISODE.name to "replace_audio",
    EpisodeFlag.REPEAT.name to "replay",
    EpisodeFlag.DOWNLOADABLE.name to "download",
    ChannelFlag.LCWA_AD_SUPPORTED_CHANNEL.name to "paid",
    ChannelFlag.PLAY_BY_PLAY.name to "sports_football",
    ChannelFlag.MATURE.name to "explicit",
    ChannelFlag.ONLY_AVAILABLE_ON_SAT.name to "satellite_alt",
)

/**
 * Render an entity list item.
 * @param includeLink if true, renders the entire item as clickable with a link to the entity page
 * @param onClick if includeLink is false, pass this if you'd like to receive a click event on the item.
 */
@Composable
fun entityListItem(app: AggregatorService, entity: Entity, includeLink: Boolean, includeId: Boolean = false,
                   includeDate: Boolean = false,
                   actions: (@Composable ()->Unit)? = null,
                   onClick: (() -> Unit)? = null) {
    @Composable
    fun entityContent() {

            Div({
                classes(AggregatorStyles.entityListItemImage)
                style {
                    width(112.px)
                    height(112.px)
                }
            }) {
                val imageUrl = entity.thumbnail?.let {
                    it.resizedUrl(
                        resize = ResizeParams.constrainToWidth(112,
                            it.width, it.height))
                }

                if (imageUrl != null) {
                    Img(src = imageUrl) {
                        style {
                            width(100.percent)
                            display(DisplayStyle.Block)
                        }
                    }
                }
            }

            Div {
                Div({
                    style {
                        marginBottom(8.px)
                    }
                }) {
                    Badge(app.entityType(entity.type)?.name ?: entity.type, BadgeColor.BLUE)
                }
                if (entity.type != "entity-image") {
                    Span({
                        style {
                            fontWeight(700)
                        }
                    }) {
                        Text(entity.label)
                    }
                }

                // Display flag icons
                val flags = entity.data["flags"] as? JsonArray
                if (flags != null && flags.isNotEmpty()) {
                    Div({ classes(AggregatorStyles.entityListIcons) }) {
                        flags.forEach { flagElement ->
                            val flagId = flagElement.jsonPrimitive.content
                            val iconName = flagIconMapping[flagId]
                            if (iconName != null) {
                                icon(iconName) {
                                    type = IconType.SYMBOLS
                                    size = IconSize.SMALL
                                    title = flagId
                                    style {
                                        opacity(.6)
                                    }
                                }
                            }
                        }
                    }
                }
                
                if (includeId) {
                    Div({ classes(AggregatorStyles.entitySubInfo) }) {
                        Text(entity.id)
                    }
                }
                if (includeDate && entity.hasTimestamp()) {
                    Div({ classes(AggregatorStyles.entitySubInfo) }) {
                        val timestamp = entity.getTimestamp()?.let {
                            val instant = Instant.parse(it)
                            // Render in the globally-selected timezone (header picker),
                            // not the browser's system zone.
                            instant.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()
                        } ?: ""
                        Text(timestamp)
                    }
                }
            }

            if (actions != null) {
                Div({
                    style {
                        position(Position.Absolute)
                        top(0.px)
                        right(0.px)
                        padding(14.px)
                        cursor("pointer")
                    }
                }) {
                    actions()
                }
            }

    }


    if (includeLink) {
        A(href = "#aggregator/entity/${encodeURIComponent(entity.id)}", {
            classes(AggregatorStyles.entityListItem)
        }) {
            entityContent()
        }
    } else {
        Div({
            classes(AggregatorStyles.entityListItem)
            if (onClick != null) {
                style {
                    cursor("pointer")
                }
                this.onClick {
                    onClick()
                }
            }
        }) {
            entityContent()
        }
    }

}

fun Entity.getTimestamp(): String? {
    return this.data.getString("originalAirTimestamp")  ?:
            this.data.getString("startTimestamp") ?:
            this.data.getString("publicationDate")
}

fun Entity.hasTimestamp(): Boolean {
    return this.data.getString("originalAirTimestamp") != null ||
        this.data.getString("startTimestamp") != null ||
        this.data.getString("publicationDate") != null
}

/**
 * Render an entity list.
 */
@Composable
fun entityList(
    app: AggregatorService,
    entities: List<Entity>,
    includeLinks: Boolean = true,
    includeIds: Boolean = false,
    includeTimestamp: Boolean = false,
    onClick: ((Entity) -> Unit)? = null
) {
    Div({ classes(AggregatorStyles.entityList) }) {
        entities.forEach { entity ->
            entityListItem(app, entity, includeLinks && onClick == null, includeIds, includeTimestamp) {
                onClick?.invoke(entity)
            }
        }
    }
}